/* @flow */
import * as React from 'react';
import { graphql } from 'react-apollo';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Container from 'components/Container';
import Copy from 'components/Copy';
import Divider from 'components/Divider';
import FamilyActions from 'actions/FamilyActions';
import gql from 'graphql-tag';
import Heading from 'components/Heading';
import Image from 'components/Image';
import LinkConstants from 'constants/LinkConstants';
import LoadingIndicator from 'components/LoadingIndicator';
import Progress from 'components/Progress';
import Row from 'components/Row';
import TitleHeader from 'components/TitleHeader';
import ToastActions from 'actions/ToastActions';
import UploadButton from 'components/UploadButton';
import { uploadFileRequest } from 'clients/api/fileUploads';
import type { V2File } from 'clients/api/fileUploads';

import './styles/ParentalConsentSignatureView.scss';

const fileUploadError = `
  There was an error uploading your photo. Please try again or email
  ${LinkConstants.SUPPORT_EMAIL}
  for help.
`;

type Props = {
  childId: number,
  formUrl: string,
  onProceed: Function,
};

type State = {
  uploadProgressPercent?: number,
};

class ParentalConsentSignatureView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { uploadProgressPercent: undefined };
  }

  onUploadProgress = (percent: number) => {
    this.setState({ uploadProgressPercent: percent });
  };

  onUploadSuccess = (file: V2File) => {
    const onSuccess = () => {
      ToastActions.showSuccessToast('Photo upload successful');
      this.props.onProceed();
    };

    const onError = () => {
      ToastActions.showErrorToast(fileUploadError);
    };

    // $FlowFixMe
    FamilyActions.sendConsentFile(this.props.childId, (file.id: string)).then(onSuccess, onError);
  };

  onUploadFailed = () => {
    ToastActions.showErrorToast(fileUploadError);
  };

  renderBody = () => {
    const { formUrl } = this.props;
    const { uploadProgressPercent } = this.state;

    if (!formUrl) {
      return <LoadingIndicator align="center" />;
    }

    if (uploadProgressPercent) {
      return (
        <Container size="xxsmall">
          <Progress percent={uploadProgressPercent} label="Uploading consent file…" />
        </Container>
      );
    } else {
      return (
        <section>
          <TitleHeader title="Print and sign a form" />
          <Row size="small">
            <Image src={require('assets/images/ParentalConsentSignatureView/form.svg')} />
          </Row>
          <Row>
            <Copy>
              <Heading level="3">Step 1: Print, read, and sign the permission form</Heading>
              <Button href={formUrl} target="_blank" level="secondary" full>
                Download Form
              </Button>
              <Divider size="medium" />
              <Heading level="3">Step 2: Upload a photo of the signed permission form</Heading>
              <p>The photo should include the entire form with the signature clearly visible.</p>
            </Copy>
          </Row>
          <UploadButton
            fileContextKey=""
            uploadRequest={(
              fileContextKey: string,
              filename: string,
              file: File,
              onSuccess?: (file: V2File) => void,
              onFailure?: (err: Error) => void,
              privateUpload?: boolean
            ) => {
              uploadFileRequest(file, privateUpload, this.onUploadProgress)
                .then(response => {
                  if (onSuccess) {
                    onSuccess(response);
                  }
                })
                .catch(err => {
                  if (onFailure) {
                    onFailure(err);
                  }
                });
            }}
            onSuccess={this.onUploadSuccess}
            onFailure={this.onUploadFailed}
            full>
            Upload
          </UploadButton>
        </section>
      );
    }
  };

  render() {
    return <BoxBody className="ParentalConsentSignatureView">{this.renderBody()}</BoxBody>;
  }
}

export { ParentalConsentSignatureView };

const withData = graphql(
  gql`
    query getVpcFormUrl($childId: Int!) {
      me {
        uuid
        family {
          vpcFormUrl(childId: $childId)
        }
      }
    }
  `,
  {
    options: ({ childId }) => ({
      variables: { childId },
    }),
    props: ({ data }) => ({
      formUrl: data.me ? data.me.family.vpcFormUrl : null,
    }),
  }
);

export default withData(ParentalConsentSignatureView);
